import axios from "axios";
import { $session } from "./session.js";

const AUTH_REFRESH_URL = "/user-app/v2/auth/token/refresh";
const AUTH_LOGOUT_URL  = "/user-app/v2/auth/logout";

const $axiosInterceptor =
    {
        _refreshPromise: null,

        /* =========================================
         * request interceptor
         * ========================================= */
        interceptorsRequest()
        {
            axios.interceptors.request.use(
                function (config)
                {
                    config.withCredentials = true;

                    return config;
                },
                function (error)
                {
                    return Promise.reject(error);
                }
            );
        },

        /* =========================================
         * response interceptor
         * ========================================= */
        interceptorsResponse()
        {
            const self = this;

            axios.interceptors.response.use(
                function (response)
                {
                    return response;
                },
                async function (error)
                {
                    if (!error || !error.response) {
                        return Promise.reject(error);
                    }

                    const status = error.response.status;
                    const originalConfig = error.config || {};

                    const isRefreshCall =
                        (originalConfig.url || "").includes("/v2/auth/token/refresh");

                    const skipAuthRefresh =
                        originalConfig._skipAuthRefresh === true;

                    /* -----------------------------------------
                     * 401 → access token 만료
                     * ----------------------------------------- */
                    if (
                        status === 401 &&
                        !originalConfig._retry &&
                        !isRefreshCall &&
                        !skipAuthRefresh
                    ) {
                        originalConfig._retry = true;

                        try {
                            await self._refreshOnce();
                            return axios(originalConfig);
                        } catch (e) {
                            await self._logoutSilently();
                            localStorage.clear();
                            location.href = $session.getSignInUrl();
                            return Promise.reject(error);
                        }
                    }

                    /* -----------------------------------------
                     * 기타 상태코드
                     * ----------------------------------------- */
                    if (status === 412) {
                        console.log("412");
                    } else {
                        self._interceptorsOther(error);
                    }

                    return Promise.reject(error);
                }
            );
        },

        /* =========================================
         * refresh 동시 호출 방지 (Promise 공유)
         * ========================================= */
        _refreshOnce()
        {
            if (!this._refreshPromise) {
                this._refreshPromise = axios.post(
                    AUTH_REFRESH_URL,
                    null,
                    {
                        withCredentials: true,
                        _skipAuthRefresh: true
                    }
                ).finally(() => {
                    this._refreshPromise = null;
                });
            }
            return this._refreshPromise;
        },

        /* =========================================
         * 로그아웃 (실패 무시)
         * ========================================= */
        async _logoutSilently()
        {
            try {
                await axios.delete(
                    AUTH_LOGOUT_URL,
                    {
                        withCredentials: true,
                        _skipAuthRefresh: true
                    }
                );
            } catch (e) {
                // ignore
            }
        },

        /* =========================================
         * 기타 오류 처리 (기존 로직 유지)
         * ========================================= */
        _interceptorsOther(error)
        {
            const status = error.response.status;

            // 403 : 권한 없음
            if (status === 403) {
                if (
                    error.response.data &&
                    error.response.data.error === "Forbidden"
                ) {
                    error.response.data.errors = [
                        { code: "ERRORS", msg: "Access role does not exist" }
                    ];
                }

                // 412 : validation
            } else if (status === 412) {
                // skip

                // 400 : bad request
            } else if (status === 400) {
                // skip

                // 기타
            } else {
                const errorData = error.response.data;

                if (errorData?.errors?.length > 0) {
                    // skip
                } else if (typeof errorData === "string") {
                    if (errorData.indexOf("ECONNREFUSED") > -1) {
                        error.response.data = {
                            errors: [
                                {
                                    code: "ERRORS",
                                    msg: "A failure has occurred in connecting to the server."
                                }
                            ]
                        };
                    }
                }
            }
        }
    };

export { $axiosInterceptor };